import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { BatchPackageState, BatchReturnSourceReturn, ReturnOrderLine, ReturnStatusType } from 'returnCenter-api';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import useBatchReturnTableRows from 'hooks/useBatchReturnTableRows';
import useBatchPackages, { BatchPackageView } from 'hooks/useBatchPackages';
import { selectCreditSelection, toggleCreditSelection } from 'store/reducers/batchReturnSlice';
import { selectCreditsBatchSlipsTogether } from 'store/reducers/configSlice';
import { getStatusColor } from 'components/Orders/OrderStatus';
import CssSphere from 'components/ui/CssSphere';
import Flex from 'components/ui/Flex';
import Table from 'components/ui/Table';
import Text from 'components/ui/Text';

type BatchPackageGroupProps = {
    sourceReturn: BatchReturnSourceReturn;
    packages: ReturnType<typeof useBatchPackages>;
    compact: boolean;
    /** Whether any package in the batch has a requested quantity: then every card shows the column. */
    showRequested: boolean;
};

/**
 * One package of a batch: the sender's source return, its lines, and where it stands with each
 * receiving company. The package is what is scanned, handled and credited, so the batch is read
 * one package at a time rather than one order at a time.
 */
const BatchPackageGroup = ({ sourceReturn, packages, compact, showRequested }: BatchPackageGroupProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const creditSelection = useAppSelector(selectCreditSelection);
    const creditsTogether = useAppSelector(selectCreditsBatchSlipsTogether);
    const {
        isSender,
        packagesFor,
        busyPackage,
        navigateToReturn,
        openReturnSlip,
        createSlip,
        toggleMissing,
        packageStateLabel,
    } = packages;

    const sourceReturnId = sourceReturn.returnId ?? 0;
    const items = useMemo(() => sourceReturn.items ?? [], [sourceReturn.items]);
    const views = useMemo(() => packagesFor(sourceReturnId), [packagesFor, sourceReturnId]);

    const orderGroups = useMemo(() => {
        const groups = new Map<string, ReturnOrderLine[]>();
        for (const line of items) {
            const orderNumber = line.orderNumber ?? 'unknown';
            groups.set(orderNumber, [...(groups.get(orderNumber) ?? []), line]);
        }
        return Array.from(groups.entries());
    }, [items]);

    const renderReturnSlip = useCallback(
        (orderline: ReturnOrderLine) => {
            const view = views.find((v) => v.company.companyCode === orderline.supplyingCompany) ?? views[0];
            if (!view) return null;
            const { pkg, canAct } = view;

            if (pkg.returnSlipId != null) {
                const label = (
                    <>
                        <CssSphere size={8} color={getStatusColor(pkg.returnSlipStatus ?? null)} />
                        <Text as="span" bold fontSize={11}>
                            #{pkg.returnSlipId}
                        </Text>
                    </>
                );
                // The slip lives in the receiving company; only that company can open it.
                return canAct ? (
                    <SlipLink type="button" onClick={() => openReturnSlip(pkg)}>
                        {label}
                    </SlipLink>
                ) : (
                    <Flex align="center" gap="0.25rem">
                        {label}
                    </Flex>
                );
            }

            if (canAct && pkg.state === BatchPackageState.NotStarted) {
                return (
                    <LinkButton
                        type="button"
                        disabled={busyPackage === pkg.sourceReturnId}
                        onClick={() => createSlip(pkg)}
                    >
                        {t('batchReturn.createSlip', 'Create')}
                    </LinkButton>
                );
            }

            return (
                <Text as="span" fontSize={11} color="textLight">
                    -
                </Text>
            );
        },
        [views, openReturnSlip, createSlip, busyPackage, t],
    );

    const { headers, getRows, colLayout } = useBatchReturnTableRows({ renderReturnSlip, showRequested });
    const rows = useMemo(() => orderGroups.flatMap(([orderNumber, lines]) => getRows(orderNumber, lines)), [
        orderGroups,
        getRows,
    ]);

    const renderStatus = ({ company, pkg, canAct }: BatchPackageView) => {
        // A failed round is retried as it was, so nothing else can be ticked meanwhile.
        const hasFailedRound = company.packages.some((p) => p.returnSlipStatus === ReturnStatusType.CreditError);
        const canSelect =
            canAct && creditsTogether && !hasFailedRound && pkg.returnSlipStatus === ReturnStatusType.BatchHandled;
        const showReceived = pkg.state === BatchPackageState.Handled || pkg.state === BatchPackageState.Credited;
        const canToggleMissing =
            canAct && (pkg.state === BatchPackageState.NotStarted || pkg.state === BatchPackageState.Missing);

        return (
            <Flex key={company.companyCode} align="center" gap="0.5rem" justify="end">
                {canSelect && (
                    <input
                        type="checkbox"
                        aria-label={t('batchReturn.selectForCredit', 'Select for credit')}
                        checked={creditSelection.includes(pkg.returnSlipId!)}
                        onChange={() => dispatch(toggleCreditSelection(pkg.returnSlipId!))}
                    />
                )}
                <Text fontSize={12} margin={0} color={pkg.deviationRows > 0 ? 'error' : 'textLight'}>
                    {views.length > 1 && `${company.companyCode} · `}
                    {packageStateLabel(pkg)}
                    {showReceived &&
                        ` · ${t('batchReturn.received', '{{received}}/{{expected}} received', {
                            received: pkg.receivedQuantity,
                            expected: pkg.expectedQuantity,
                        })}`}
                </Text>
                {canToggleMissing && (
                    <LinkButton
                        type="button"
                        disabled={busyPackage === pkg.sourceReturnId}
                        onClick={() => toggleMissing(pkg)}
                    >
                        {pkg.state === BatchPackageState.Missing
                            ? t('batchReturn.undoMissing', 'Undo')
                            : t('batchReturn.markMissing', 'Mark missing')}
                    </LinkButton>
                )}
            </Flex>
        );
    };

    return (
        <Card column>
            <Header justify="between" align="center" gap="1rem">
                {/* One line: the title and what the package is, so the card stays as low as a table row. */}
                <Flex align="baseline" gap="0.75rem" $wrap>
                    <Text bold fontSize={15} margin={0}>
                        {t('return', 'Return')}{' '}
                        {/* The source return lives in the sending company; only it can open it. */}
                        {isSender ? (
                            <TitleLink
                                type="button"
                                onClick={() => navigateToReturn(sourceReturnId, sourceReturn.returnStatus)}
                            >
                                #{sourceReturnId}
                            </TitleLink>
                        ) : (
                            `#${sourceReturnId}`
                        )}
                        {sourceReturn.claimReference ? ` · ${sourceReturn.claimReference}` : ''}
                    </Text>
                    <Text fontSize={12} margin={0} color="textLight">
                        {[
                            sourceReturn.customerCode,
                            `${t('order', 'Order')}: ${orderGroups.map(([orderNumber]) => orderNumber).join(', ')}`,
                            `${items.length} ${t('items', 'items')}`,
                        ]
                            .filter(Boolean)
                            .join(' · ')}
                    </Text>
                </Flex>
                <Flex column gap="0.25rem">
                    {views.map(renderStatus)}
                </Flex>
            </Header>

            {rows.length > 0 ? (
                <Table
                    className={`package-${sourceReturnId}-table f3-300`}
                    compact={compact}
                    headers={headers}
                    cols={headers.length}
                    rows={rows}
                    bgColor="fillWhite"
                    colLayout={colLayout}
                />
            ) : (
                <Text as="p" fontSize={14} className="text-center">
                    {t('batchReturns.noItems', 'No items found for this batch return.')}
                </Text>
            )}
        </Card>
    );
};

export default BatchPackageGroup;

const Card = styled(Flex)`
    padding: 0.5rem 0;
`;

const Header = styled(Flex)`
    padding: 0.5rem 1rem;
    background-color: ${({ theme }) => theme.colors.fillWhite};
    border-radius: var(--radius);
    margin-bottom: 0.25rem;
`;

const LinkButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    font-size: 11px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.link};
    cursor: pointer;
    white-space: nowrap;
    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

const SlipLink = styled(LinkButton)`
    display: flex;
    align-items: center;
    gap: 0.25rem;
`;

const TitleLink = styled.button`
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    color: ${({ theme }) => theme.colors.link};
    cursor: pointer;
    text-decoration: underline;
`;
